import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Compass, FilePlus2, History, Lightbulb, Lock, Sparkles, Wand2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { usePlan } from '../hooks/usePlan';
import { PLANBASE_CONFIG } from '../config';

function inlineMarkdown(value: string) {
  return value.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={index}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*')) return <em key={index}>{part.slice(1, -1)}</em>;
    return part;
  });
}

function RichBrief({ content }: { content: string }) {
  return (
    <div className="space-y-3 text-sm leading-6 text-foreground">
      {content.split(/\r?\n/).map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) return <div key={index} className="h-1" />;
        if (trimmed.startsWith('#')) return <h3 key={index} className="text-sm font-semibold text-foreground mt-4 first:mt-0">{inlineMarkdown(trimmed.replace(/^#+\s*/, ''))}</h3>;
        if (/^[-*]\s+/.test(trimmed)) return <div key={index} className="flex gap-2"><span className="text-orange-500">•</span><span>{inlineMarkdown(trimmed.replace(/^[-*]\s+/, ''))}</span></div>;
        if (/^\d+[.)]\s+/.test(trimmed)) return <div key={index} className="flex gap-2"><span className="font-medium text-orange-500">{trimmed.match(/^\d+/)?.[0]}.</span><span>{inlineMarkdown(trimmed.replace(/^\d+[.)]\s+/, ''))}</span></div>;
        return <p key={index}>{inlineMarkdown(trimmed)}</p>;
      })}
    </div>
  );
}

const ideas = [
  'Describe a compact residential project in a warm modern style.',
  'Suggest a stronger story for an adaptive reuse project.',
  'Create a practical floor-plan brief for a two-bedroom home.',
];

interface ChatMessage { role: 'user' | 'assistant'; content: string; image_url?: string; image_urls?: string[]; }
interface ChatSession { id: string; title: string; created_at: string; updated_at: string; }

export default function OrbitAI() {
  const { user } = useAuth();
  const { isPro, planLoading } = usePlan();
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generatingImage, setGeneratingImage] = useState(false);
  const [imageType, setImageType] = useState('concept');
  const [imageCount, setImageCount] = useState(2);
  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [sessionId, setSessionId] = useState('');
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedSessionId = searchParams.get('session');
  const isPaid = isPro || ['pro', 'enterprise'].includes(String(user?.metadata?.tier || user?.metadata?.plan || '').toLowerCase());

  const authHeaders = () => ({ 'Content-Type': 'application/json', 'X-Project-ID': PLANBASE_CONFIG.projectId, 'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`, 'X-User-Token': localStorage.getItem('planbase_token') || '' });

  const loadSessions = async () => {
    try {
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai/sessions`, { headers: authHeaders() });
      const data = await response.json();
      if (response.ok) setSessions(data.sessions || []);
    } catch {}
  };

  useEffect(() => {
    if (!planLoading && isPaid) {
      loadSessions();
      if (requestedSessionId) loadSession(requestedSessionId);
    }
  }, [planLoading, isPaid, requestedSessionId]);

  if (planLoading) return <div className="h-96 bg-muted rounded-2xl animate-pulse" />;
  if (!isPaid) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h1 className="text-sm font-semibold text-foreground uppercase tracking-wide">Orbi AI</h1>
        </div>
        <div className="h-[calc(100vh-12rem)] w-full flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-8">
            <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-5">
              <Lock className="h-7 w-7 text-orange-500" />
            </div>
            <h2 className="text-sm font-medium text-foreground mb-3">Orbi AI is a Premium Feature</h2>
            <p className="text-sm text-muted-foreground font-normal mb-6">Upgrade to shape projects, refine your portfolio, and unlock design guidance.</p>
            <Button onClick={() => navigate('/pricing')} className="bg-orange-500 hover:bg-orange-600 text-white px-6">View plans</Button>
          </div>
        </div>
      </div>
    );
  }

  const runBrief = async (value: string) => {
    setPrompt(value); setGenerating(true); setError('');
    try {
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Project-ID': PLANBASE_CONFIG.projectId, 'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`, 'X-User-Token': localStorage.getItem('planbase_token') || '' },
        body: JSON.stringify({ action: value.toLowerCase().includes('floor') ? 'floor_plan_brief' : 'project_brief', prompt: value, useArchitectData: true }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to generate a brief');
      const assistantMessage = data.result || 'No result was returned.';
      const nextMessages = [...chatMessages, { role: 'user' as const, content: value }, { role: 'assistant' as const, content: assistantMessage }];
      setResult(assistantMessage); setChatMessages(nextMessages); await saveSession(nextMessages);
    } catch (err: any) { setError(err.message || 'Unable to generate a brief'); }
    finally { setGenerating(false); }
  };

  const saveSession = async (messages: ChatMessage[]) => {
    try {
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai/sessions${sessionId ? `/${sessionId}` : ''}`, {
        method: sessionId ? 'PATCH' : 'POST', headers: authHeaders(), body: JSON.stringify({ messages }),
      });
      const data = await response.json();
      if (!response.ok) return;
      setSessionId(data.session.id);
      setSearchParams({ session: data.session.id }, { replace: true });
      setSessions(current => [data.session, ...current.filter(session => session.id !== data.session.id)]);
    } catch {}
  };

  const loadSession = async (id: string) => {
    setError('');
    try {
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai/sessions/${id}`, { headers: authHeaders() });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Unable to load chat');
      const messages = Array.isArray(data.session.messages) ? data.session.messages : [];
      setSessionId(id); setChatMessages(messages);
      setSearchParams({ session: id }, { replace: true });
      setPrompt(messages.filter((message: ChatMessage) => message.role === 'user').at(-1)?.content || '');
      setResult(messages.filter((message: ChatMessage) => message.role === 'assistant' && !message.image_urls?.length && !message.image_url).at(-1)?.content || '');
      const imageMessage = messages.filter((message: ChatMessage) => message.image_urls?.length || message.image_url).at(-1);
      setImageUrls(imageMessage?.image_urls || (imageMessage?.image_url ? [imageMessage.image_url] : []));
    } catch (err: any) { setError(err.message || 'Unable to load chat'); }
  };

  const generateImage = async () => {
    if (!prompt.trim()) return;
    setGeneratingImage(true); setError('');
    try {
      const response = await fetch(`${PLANBASE_CONFIG.apiUrl}/sdk/planbase/ai/image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Project-ID': PLANBASE_CONFIG.projectId, 'Authorization': `Bearer ${PLANBASE_CONFIG.apiKey}`, 'X-User-Token': localStorage.getItem('planbase_token') || '' },
          body: JSON.stringify({ prompt, imageType, count: imageCount }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Unable to generate image');
      const nextImageUrls = Array.isArray(data.urls) ? data.urls : data.url ? [data.url] : [];
      setImageUrls(nextImageUrls);
      const nextMessages = [...chatMessages, { role: 'user' as const, content: prompt }, { role: 'assistant' as const, content: `Generated ${nextImageUrls.length} architectural image${nextImageUrls.length === 1 ? '' : 's'}.`, image_urls: nextImageUrls }];
      setChatMessages(nextMessages);
      await saveSession(nextMessages);
    } catch (err: any) { setError(err.message || 'Unable to generate image'); }
    finally { setGeneratingImage(false); }
  };
  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4"><div><p className="text-xs uppercase tracking-wide text-orange-500 font-medium">Creative workspace</p><h1 className="text-2xl font-semibold text-foreground mt-1">Orbi AI</h1><p className="text-sm text-muted-foreground mt-1">Move from a rough idea to a stronger project story.</p></div><Sparkles className="h-5 w-5 text-orange-500" /></div>
      {sessions.length > 0 && <div className="bg-card border border-border rounded-2xl p-4"><div className="flex items-center gap-2 mb-3"><History className="h-4 w-4 text-orange-500" /><p className="text-sm font-medium text-foreground">Recent chats</p></div><div className="flex gap-2 overflow-x-auto pb-1">{sessions.map(session => <button key={session.id} onClick={() => loadSession(session.id)} className={`min-w-48 max-w-64 text-left border rounded-xl px-3 py-2 transition-colors ${session.id === sessionId ? 'border-orange-500 bg-orange-50/50 dark:bg-orange-950/20' : 'border-border hover:border-orange-400'}`}><p className="text-xs font-medium text-foreground truncate">{session.title}</p><p className="text-[10px] text-muted-foreground mt-1">{new Date(session.updated_at).toLocaleDateString()}</p></button>)}</div></div>}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {[
          { icon: FilePlus2, title: 'New project', text: 'Start a project entry', action: () => navigate('/upload-project') },
          { icon: Compass, title: 'Portfolio profile', text: 'Shape your public story', action: () => navigate('/portfolio-setup') },
          { icon: Wand2, title: 'Project direction', text: 'Get a structured brief', action: () => runBrief(ideas[0]) },
        ].map(({ icon: Icon, title, text, action }) => <button key={title} onClick={action} className="text-left bg-card border border-border rounded-2xl p-4 hover:border-orange-400 transition-colors"><Icon className="h-5 w-5 text-orange-500 mb-5" /><p className="text-sm font-medium text-foreground">{title}</p><p className="text-xs text-muted-foreground mt-1">{text}</p><ArrowRight className="h-4 w-4 text-muted-foreground mt-4" /></button>)}
      </div>
      <div className="bg-card border border-border rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-3"><Lightbulb className="h-4 w-4 text-orange-500" /><p className="text-sm font-medium text-foreground">Ask for a direction</p></div>
        <textarea value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="e.g. Give me a floor-plan brief for a compact two-bedroom home" rows={3} className="w-full px-3 py-2 border border-input rounded-xl bg-background text-foreground text-sm resize-y focus:outline-none focus:border-orange-500" />
        <div className="flex flex-wrap items-center gap-2 mt-3">{ideas.map(idea => <button key={idea} onClick={() => runBrief(idea)} disabled={generating || generatingImage} className="text-xs text-muted-foreground border border-border rounded-lg px-3 py-2 hover:text-foreground hover:bg-muted text-left disabled:opacity-50">{idea}</button>)}<select value={imageType} onChange={e => setImageType(e.target.value)} className="text-xs bg-background border border-border rounded-lg px-2 py-2 text-foreground"><option value="concept">Concept view</option><option value="floor_plan">Floor plan</option><option value="site_plan">Site plan</option><option value="elevation">Elevation</option><option value="section">Section</option></select><select value={imageCount} onChange={e => setImageCount(Number(e.target.value))} className="text-xs bg-background border border-border rounded-lg px-2 py-2 text-foreground"><option value="1">1 image</option><option value="2">2 images</option><option value="3">3 images</option><option value="4">4 images</option></select><Button onClick={() => runBrief(prompt)} disabled={!prompt.trim() || generating || generatingImage} size="sm" className="bg-orange-500 hover:bg-orange-600 text-white ml-auto">{generating ? 'Generating...' : 'Generate brief'}</Button><Button onClick={generateImage} disabled={!prompt.trim() || generating || generatingImage} size="sm" variant="outline">{generatingImage ? 'Generating...' : 'Generate images'}</Button></div>
        {error && <p className="mt-4 text-sm text-destructive">{error}</p>}
        {result && <div className="mt-5 border-t border-border pt-4"><p className="text-xs uppercase tracking-wide text-orange-500 font-medium mb-2">Orbit brief</p><RichBrief content={result} /></div>}
        {imageUrls.length > 0 && <div className="mt-5 border-t border-border pt-4"><p className="text-xs uppercase tracking-wide text-orange-500 font-medium mb-2">Generated images</p><div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{imageUrls.map((url, index) => <img key={url} src={url} alt={`Generated architectural image ${index + 1}`} className="w-full aspect-[4/3] object-cover rounded-xl border border-border" />)}</div></div>}
      </div>
    </div>
  );
}

